import React, { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTour } from '../../context/TourContext';
import tourSteps, { resolveSelector, resolvePlacement } from './tourSteps';
import '../../styles/tour.css';

interface Rect { top: number; left: number; width: number; height: number; }

const GAP = 10;
const MOBILE_BREAKPOINT = 769;

function getRect(selector: string): Rect | null {
    const el = document.querySelector(selector);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { top: r.top - GAP, left: r.left - GAP, width: r.width + GAP * 2, height: r.height + GAP * 2 };
}

function tooltipPosition(rect: Rect, placement: string, tooltipW: number, tooltipH: number) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = 0, left = 0;

    if (placement === 'bottom') {
        top = rect.top + rect.height + 12;
        left = rect.left;
    } else if (placement === 'top') {
        top = rect.top - tooltipH - 12;
        left = rect.left;
    } else if (placement === 'right') {
        top = rect.top;
        left = rect.left + rect.width + 12;
    } else if (placement === 'left') {
        top = rect.top;
        left = rect.left - tooltipW - 12;
    }

    left = Math.max(12, Math.min(left, vw - tooltipW - 12));
    top = Math.max(12, Math.min(top, vh - tooltipH - 12));

    return { top, left };
}

const TourOverlay: React.FC = () => {
    const { t } = useTranslation('tour');
    const { isTourActive, currentStep, totalSteps, nextStep, prevStep, endTour } = useTour();
    const [spotRect, setSpotRect] = useState<Rect | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

    const step = tourSteps[currentStep];
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    const resolvedSelector = step ? resolveSelector(step, isMobile) : null;
    const resolvedPlacement = step ? resolvePlacement(step, isMobile) : 'center';
    const isCenter = resolvedPlacement === 'center' || !resolvedSelector;
    const isLastStep = currentStep === totalSteps - 1;

    const updatePosition = useCallback(() => {
        const mobile = window.innerWidth < MOBILE_BREAKPOINT;
        if (!step) return;
        const selector = resolveSelector(step, mobile);
        if (!selector) return;
        const rect = getRect(selector);
        if (!rect) return;
        setSpotRect(rect);
        const placement = resolvePlacement(step, mobile);
        const pos = tooltipPosition(rect, placement, 300, 200);
        setTooltipPos(pos);
    }, [step]);

    useLayoutEffect(() => {
        if (!isTourActive || isCenter) {
            setSpotRect(null);
            return;
        }
        updatePosition();
    }, [isTourActive, currentStep, isCenter, updatePosition]);

    useEffect(() => {
        if (!isTourActive || isCenter) return;
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        const ro = new ResizeObserver(updatePosition);
        ro.observe(document.body);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            ro.disconnect();
        };
    }, [isTourActive, isCenter, updatePosition]);

    if (!isTourActive || !step) return null;

    const tooltipContent = (
        <>
            <span className="tour-step-badge">{currentStep + 1} / {totalSteps}</span>
            {isLastStep && <span className="tour-done-icon">🎉</span>}
            <h3 className="tour-tooltip-title">{t(step.titleKey)}</h3>
            <p className="tour-tooltip-text">{t(step.textKey)}</p>
            <div className="tour-buttons">
                {!isLastStep && (
                    <button type="button" className="tour-skip-btn" onClick={endTour}>
                        {t('skip')}
                    </button>
                )}
                {currentStep > 0 && !isLastStep && (
                    <button type="button" className="tour-back-btn" onClick={prevStep}>
                        {t('back')}
                    </button>
                )}
                {isLastStep ? (
                    <button type="button" className="tour-done-btn" onClick={endTour}>
                        {t('done')}
                    </button>
                ) : (
                    <button type="button" className="tour-next-btn" onClick={nextStep}>
                        {t('next')}
                    </button>
                )}
            </div>
        </>
    );

    if (isCenter) {
        return (
            <div className="tour-backdrop" onClick={endTour}>
                <div
                    className="tour-tooltip tour-tooltip-center"
                    onClick={e => e.stopPropagation()}
                >
                    {tooltipContent}
                </div>
            </div>
        );
    }

    return (
        <>
            {spotRect && (
                <div
                    className="tour-spotlight"
                    style={{ top: spotRect.top, left: spotRect.left, width: spotRect.width, height: spotRect.height }}
                />
            )}
            <div
                className={`tour-tooltip placement-${resolvedPlacement}`}
                style={{ top: tooltipPos.top, left: tooltipPos.left }}
            >
                {tooltipContent}
            </div>
        </>
    );
};

export default TourOverlay;
